import React, { useEffect, useState } from 'react';
import { Building2, User } from 'lucide-react';
import { resolveDisplayLogoUrl } from '../../lib/utils/companyLogoService';

interface ClientAvatarProps {
  client: {
    logo_url?: string | null;
    business_name?: string;
    entity_type?: 'individual' | 'company';
    /** Usati per il logo dal dominio quando logo_url manca */
    email?: string | null;
    website?: string | null;
  };
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
}

const sizeMap = {
  xs: { container: 'w-6 h-6', icon: 'w-3 h-3', text: 'text-[8px]' },
  sm: { container: 'w-8 h-8', icon: 'w-4 h-4', text: 'text-[10px]' },
  md: { container: 'w-10 h-10', icon: 'w-5 h-5', text: 'text-xs' },
  lg: { container: 'w-14 h-14', icon: 'w-6 h-6', text: 'text-sm' },
};

function getInitials(name?: string): string {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase())
    .filter(Boolean)
    .join('');
}

export function ClientAvatar({ client, size = 'md', className = '', onClick }: ClientAvatarProps) {
  const logoSrc = resolveDisplayLogoUrl(client);
  const [imgError, setImgError] = useState(false);
  // Righe riusate (liste virtualizzate/paginate): il nuovo logo va riprovato
  useEffect(() => { setImgError(false); }, [logoSrc]);
  const s = sizeMap[size];
  const isCompany = client.entity_type === 'company';

  const baseClasses = `${s.container} rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${className}`;
  const clickableClass = onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : '';

  // Show logo if available and not errored
  if (logoSrc && !imgError) {
    return (
      <div className={`${baseClasses} ${clickableClass}`} onClick={onClick}>
        <img
          src={logoSrc}
          loading="lazy"
          alt={client.business_name || ''}
          className="w-full h-full object-cover rounded-full"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  // Fallback: initials with icon
  const initials = getInitials(client.business_name);
  const Icon = isCompany ? Building2 : User;

  return (
    <div
      className={`${baseClasses} bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 ${clickableClass}`}
      onClick={onClick}
    >
      {initials.length > 0 && initials !== '?' ? (
        <span className={`${s.text} font-semibold`}>{initials}</span>
      ) : (
        <Icon className={s.icon} />
      )}
    </div>
  );
}
